import styled from '@emotion/styled'
import {
  ExtranetLanguageOption,
  ExtranetOrderHistoryItemDto,
  ExtranetOrderHistoryListItemDto,
  ExtranetShopOrderItemStatus,
  ExtranetShopOrderStatusEnum,
} from '@pharmac-extranet/extranet-api'
import {
  CheckIcon,
  ExtranetCatalogItemChevron,
  ExtranetContainer,
  ExtranetExpandArrowColumn,
  ExtranetTable,
  ExtranetTableColumn,
  ExtranetTableRow,
  formatDateString,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import {
  PharmacButton,
  PharmacErrorToast,
  PharmacIcon,
  PharmacLabel,
  PharmacSuccessToast,
  PharmacText,
  usePharmacToast,
} from '@pharmac/ui'
import { useQuery } from '@tanstack/react-query'
import React, { useState } from 'react'
import { formatPrice } from '../../../components/physical-products/util/price-utils'
import { useCustomerContext } from '../../../features/customer/CustomerProvider'
import { api } from '../../../services/HttpService'

const DataPairLine = styled.div`
  height: 1px;
  background-color: var(--color-accent-3);
  flex: 1 1 auto;
  margin: 0 var(--spacing-medium);
  min-width: 10px;
  flex: 1 0 auto;
`

const CopyClipBoardButton = styled(PharmacButton)`
  max-height: 32px;
  margin-left: auto;
`

const ActivationCodesBanner = styled.div`
  height: 41px;
  background-color: #f3e9f499;
  align-items: center;
  display: flex;
  margin-top: var(--spacing-medium);
`

const Dashes = styled.div`
  width: 100%;
  height: 1px;

  background-image: repeating-linear-gradient(
    90deg,
    #d9d9dd 0px,
    #d9d9dd 6px,
    transparent 4px,
    transparent 12px
  );
`
const ActivationCodeRow = styled.div`
  display: grid;
  grid-template-columns: 2.7fr 1fr 1fr 1.2fr 2.5fr;
  gap: var(--spacing-medium);
  align-items: center;
`

type ShopOrderHistoryRowItemsProps = {
  order: ExtranetOrderHistoryListItemDto
  statusTranslations: (
    status:
      | ExtranetShopOrderStatusEnum
      | ExtranetShopOrderItemStatus
      | 'arrived'
      | 'cancelled'
      | 'being-processed'
      | 'completed'
      | 'delivered'
      | 'partially-delivered'
      | 'sent'
  ) => string
}

type OrderItemRowContentProps = {
  data: ExtranetOrderHistoryItemDto
}

type OrderItemRowProps = {
  data: ExtranetOrderHistoryItemDto
  toggleCallback: (id: number) => void
  expandedOrderItemRows: number[]
}

export const ShopOrderHistoryRowItems = ({
  order,
  statusTranslations,
}: ShopOrderHistoryRowItemsProps) => {
  const { minDesktop } = useBreakpoint()
  const { customer } = useCustomerContext()
  const { t, i18n } = useLanguage(['extranet', 'common'])
  const { add } = usePharmacToast()

  const { data, isLoading } = useQuery({
    queryKey: ['__ShopOrderHistoryDetails', order.orderNumber, customer!.id],
    queryFn: async () =>
      api.shop.findProductOrderHistoryItems(Number(customer?.id), order.orderNumber, {
        language: i18n.language as ExtranetLanguageOption,
      }),
    enabled: !!customer?.id,
  })

  const [expandedOrderItemRows, setexpandedOrderItemRows] = useState<number[]>([])
  const handleNestedExpand = (id: number) => {
    setexpandedOrderItemRows((prev) =>
      prev.includes(id) ? prev.filter((it) => it !== id) : [...prev, id]
    )
  }

  if (isLoading || !data) {
    return null
  }

  const copyToClipBoard = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value)
      add({
        toast: <PharmacSuccessToast>{t('clipboardSuccess', { ns: 'common' })}</PharmacSuccessToast>,
      })
    } catch (error: any) {
      add({
        toast: <PharmacErrorToast>{t('clipboardError', { ns: 'common' })}</PharmacErrorToast>,
      })
    }
  }

  const OrderItemRowExpandContent = ({ data }: OrderItemRowContentProps) => {
    if (!data.activationCodes) return null

    return (
      <>
        {data.activationCodes.length > 0 && (
          <ActivationCodesBanner>
            <ExtranetContainer
              alignCenter
              full
            >
              <PharmacLabel marginMediumLeft>{t('shop.orders.codes.activationCodes')}</PharmacLabel>
            </ExtranetContainer>
          </ActivationCodesBanner>
        )}

        {minDesktop ? (
          <ExtranetContainer
            flexColumn
            full
          >
            {data.activationCodes.map((code, index) => {
              return (
                <React.Fragment key={`${index}-activation-code-row`}>
                  {index !== 0 ? (
                    <>
                      <Dashes />
                    </>
                  ) : null}
                  <ExtranetContainer marginMediumVertical>
                    <ActivationCodeRow>
                      <ExtranetContainer
                        style={{ minWidth: 0 }}
                        marginMediumLeft
                      >
                        {code.code}
                      </ExtranetContainer>
                      <ExtranetContainer style={{ minWidth: 0 }}>
                        <CopyClipBoardButton
                          variant="secondary"
                          disabled={code.isUsed}
                          onClick={() => copyToClipBoard(code.code)}
                          marginLargeRight
                        >
                          <PharmacIcon icon="copy" />
                          <PharmacText
                            marginSmallLeft
                            weight="bold"
                          >
                            {t('shop.orders.codes.copy')}
                          </PharmacText>
                        </CopyClipBoardButton>
                      </ExtranetContainer>

                      <ExtranetContainer style={{ minWidth: 0 }}>
                        {code.isUsed ? (
                          <ExtranetContainer>
                            <CheckIcon
                              icon="xCircle"
                              stroke="#F2AE43"
                            />
                            <PharmacText>{t('shop.orders.codes.used')}</PharmacText>
                          </ExtranetContainer>
                        ) : (
                          <ExtranetContainer>
                            <CheckIcon icon="checkCircle" />
                            <PharmacText>{t('shop.orders.codes.unused')}</PharmacText>
                          </ExtranetContainer>
                        )}
                      </ExtranetContainer>

                      <ExtranetContainer style={{ minWidth: 0 }}>
                        {code.isUsed ? (
                          <>
                            <PharmacIcon
                              icon="user"
                              style={{ color: 'var(--color-primary)' }}
                            />
                            {code.usedBy ? (
                              <PharmacText>
                                {code.usedBy.firstName} {code.usedBy.lastName}
                              </PharmacText>
                            ) : (
                              <PharmacText marginSmallLeft>
                                {t('shop.orders.codes.externalUser')}
                              </PharmacText>
                            )}
                          </>
                        ) : (
                          <></>
                        )}
                      </ExtranetContainer>

                      <ExtranetContainer style={{ minWidth: 0 }}>
                        {code.isUsed ? (
                          <>
                            <PharmacIcon
                              icon="flag"
                              style={{ color: 'var(--color-primary)' }}
                            />

                            {code.unlimitedAccess && !code.subscriptionEndDate ? (
                              <PharmacText marginSmallLeft>
                                {t('shop.product.unlimitedAccess')}
                              </PharmacText>
                            ) : (
                              <>
                                <PharmacText marginSmallLeft>
                                  {t('shop.product.viewable')}
                                </PharmacText>
                                <PharmacText marginSmallLeft>
                                  {code.subscriptionStartDate
                                    ? formatDateString(code.subscriptionStartDate)
                                    : ''}
                                </PharmacText>
                                <PharmacText marginSmallLeft> {'-'} </PharmacText>
                                <PharmacText marginSmallLeft>
                                  {code.subscriptionEndDate
                                    ? formatDateString(code.subscriptionEndDate)
                                    : ''}
                                </PharmacText>
                              </>
                            )}
                          </>
                        ) : (
                          <></>
                        )}
                      </ExtranetContainer>
                    </ActivationCodeRow>
                  </ExtranetContainer>
                </React.Fragment>
              )
            })}
          </ExtranetContainer>
        ) : (
          /* MOBILE */
          <>
            {data.activationCodes.map((code, index) => {
              return (
                <>
                  {/*** */}
                  <ExtranetContainer
                    flexColumn
                    marginMediumLeft
                    marginMediumTop
                    marginMediumRight
                  >
                    {index !== 0 ? (
                      <>
                        <Dashes />
                      </>
                    ) : null}

                    <ExtranetContainer
                      marginSmallBottom
                      marginSmallTop
                    >
                      <PharmacText>{code.code}</PharmacText>
                    </ExtranetContainer>

                    <ExtranetContainer
                      alignCenter
                      full
                    >
                      {code.isUsed ? (
                        <>
                          <CheckIcon
                            icon="xCircle"
                            stroke="#F2AE43"
                          />
                          <PharmacText>{t('shop.orders.codes.used')}</PharmacText>
                        </>
                      ) : (
                        <>
                          <ExtranetContainer>
                            <CheckIcon icon="checkCircle" />
                            <PharmacText marginMediumLeft>
                              {t('shop.orders.codes.unused')}
                            </PharmacText>
                          </ExtranetContainer>
                        </>
                      )}

                      <ExtranetContainer full>
                        <CopyClipBoardButton
                          variant="secondary"
                          disabled={code.isUsed}
                          onClick={() => copyToClipBoard(code.code)}
                        >
                          <PharmacIcon icon="copy" />
                          <PharmacText
                            marginSmallLeft
                            weight="bold"
                          >
                            {t('shop.orders.codes.copy')}
                          </PharmacText>
                        </CopyClipBoardButton>
                      </ExtranetContainer>
                    </ExtranetContainer>

                    <ExtranetContainer alignCenter>
                      {code.isUsed ? (
                        <ExtranetContainer flexColumn>
                          <ExtranetContainer></ExtranetContainer>
                          <ExtranetContainer alignCenter>
                            <PharmacIcon
                              icon="user"
                              style={{ color: 'var(--color-primary)' }}
                            />
                            {code.usedBy ? (
                              <>
                                <PharmacText marginSmallLeft>{code.usedBy.firstName}</PharmacText>
                                <PharmacText marginSmallLeft>{code.usedBy.lastName}</PharmacText>
                              </>
                            ) : (
                              <PharmacText marginSmallLeft>
                                {t('shop.orders.codes.externalUser')}
                              </PharmacText>
                            )}
                          </ExtranetContainer>
                          <ExtranetContainer marginSmallTop>
                            <PharmacIcon
                              icon="flag"
                              style={{ color: 'var(--color-primary)' }}
                            />
                            <ExtranetContainer flexColumn>
                              {code.unlimitedAccess ? (
                                <PharmacText marginSmallLeft>
                                  {t('shop.product.unlimitedAccess')}
                                </PharmacText>
                              ) : (
                                <>
                                  <PharmacText marginSmallLeft>
                                    {t('shop.product.viewable')}
                                  </PharmacText>
                                  <ExtranetContainer>
                                    <PharmacText marginSmallLeft>
                                      {code.subscriptionStartDate
                                        ? formatDateString(code.subscriptionStartDate)
                                        : ''}
                                      -
                                    </PharmacText>
                                    <PharmacText marginSmallLeft>
                                      {code.subscriptionEndDate
                                        ? formatDateString(code.subscriptionEndDate)
                                        : ''}
                                    </PharmacText>
                                  </ExtranetContainer>
                                </>
                              )}
                            </ExtranetContainer>
                          </ExtranetContainer>
                        </ExtranetContainer>
                      ) : null}
                    </ExtranetContainer>
                  </ExtranetContainer>
                  {/*** */}
                </>
              )
            })}
          </>
        )}
      </>
    )
  }

  const OrderItemRow = ({
    data,
    toggleCallback,
    expandedOrderItemRows: isExpanded,
  }: OrderItemRowProps) => {
    const canExpand = data.activationCodes.length > 0

    const handleToggleExpand = () => {
      if (toggleCallback) toggleCallback(data.orderRowNumber)
    }

    const expanded = isExpanded?.includes(data.orderRowNumber)

    return (
      <>
        <ExtranetContainer style={{ backgroundColor: 'var(--color-accent-1)' }}>
          {minDesktop ? (
            <>
              <ExtranetTableRow
                onClick={handleToggleExpand}
                isExpanded={expanded}
                key={`${data.orderRowNumber}-row-item`}
                style={{
                  fontWeight: 300,
                }}
                expand={canExpand ? <OrderItemRowExpandContent data={data} /> : false}
                toggleExpand={() => null}
                children={({ isExpanded }) => (
                  <>
                    <ExtranetTableColumn size={1}>{data.orderRowNumber}</ExtranetTableColumn>
                    <ExtranetTableColumn size={2}>{data.productName}</ExtranetTableColumn>
                    <ExtranetTableColumn size={1}>{data.quantity}</ExtranetTableColumn>
                    <ExtranetTableColumn size={1}>
                      {formatPrice(data.itemPrice)}
                    </ExtranetTableColumn>
                    <ExtranetTableColumn size={1}>
                      {formatPrice(data.itemTotalPrice)}
                    </ExtranetTableColumn>
                    <ExtranetTableColumn size={1}>
                      {statusTranslations(data.status)}
                    </ExtranetTableColumn>
                    <ExtranetTableColumn size={1}> {data.deliveryId}</ExtranetTableColumn>
                    <ExtranetTableColumn size={0.5}>
                      {canExpand ? <ExtranetCatalogItemChevron isExpanded={isExpanded} /> : null}
                    </ExtranetTableColumn>
                  </>
                )}
              ></ExtranetTableRow>
            </>
          ) : (
            <>
              <ExtranetTableRow
                onClick={handleToggleExpand}
                isExpanded={expanded}
                key={`${data.orderRowNumber}-row-item-mobile`}
                expand={
                  <>
                    <ExtranetContainer
                      alignCenter
                      marginMediumHorizontal
                      marginSmallBottom
                    >
                      <PharmacText weight="bold"> {t('shop.orders.orderRow')} </PharmacText>
                      <DataPairLine />
                      <PharmacText> {data.orderRowNumber} </PharmacText>
                    </ExtranetContainer>

                    <ExtranetContainer
                      alignCenter
                      marginMediumHorizontal
                      marginSmallBottom
                    >
                      <PharmacText weight="bold"> {t('shop.orders.quantity')} </PharmacText>
                      <DataPairLine />
                      <PharmacText> {data.quantity} </PharmacText>
                    </ExtranetContainer>

                    <ExtranetContainer
                      alignCenter
                      marginMediumHorizontal
                      marginSmallBottom
                    >
                      <PharmacText weight="bold"> {t('shop.orders.unitPrice')}</PharmacText>
                      <DataPairLine />
                      <PharmacText> {formatPrice(data.itemPrice)} </PharmacText>
                    </ExtranetContainer>

                    <ExtranetContainer
                      alignCenter
                      marginMediumHorizontal
                      marginSmallBottom
                    >
                      <PharmacText weight="bold"> {t('shop.orders.totalPrice')}</PharmacText>
                      <DataPairLine />
                      <PharmacText> {formatPrice(data.itemTotalPrice)} </PharmacText>
                    </ExtranetContainer>

                    <ExtranetContainer
                      alignCenter
                      marginMediumHorizontal
                      marginSmallBottom
                    >
                      <PharmacText weight="bold"> {t('shop.orders.status')} </PharmacText>
                      <DataPairLine />
                      <PharmacText> {statusTranslations(data.status)} </PharmacText>
                    </ExtranetContainer>

                    <ExtranetContainer
                      alignCenter
                      marginMediumHorizontal
                      marginSmallBottom
                    >
                      <PharmacText weight="bold"> {t('shop.orders.deliveryNumber')} </PharmacText>
                      <DataPairLine />
                      <PharmacText>{data.deliveryId}</PharmacText>
                    </ExtranetContainer>
                    <OrderItemRowExpandContent data={data} />
                  </>
                }
                toggleExpand={() => null}
                children={({ isExpanded }) => (
                  <>
                    <ExtranetTableColumn size={11}>{data.productName}</ExtranetTableColumn>

                    <ExtranetExpandArrowColumn size={1}>
                      <ExtranetCatalogItemChevron isExpanded={isExpanded} />
                    </ExtranetExpandArrowColumn>
                  </>
                )}
              ></ExtranetTableRow>
            </>
          )}
        </ExtranetContainer>
      </>
    )
  }

  return (
    <ExtranetContainer
      full
      justifyCenter
      style={{ backgroundColor: 'var(--color-accent-1) ' }}
    >
      <ExtranetContainer
        marginMediumTop
        full
        marginLargeHorizontal
      >
        <ExtranetTable style={{ fontFamily: 'var(--font-family-secondary)', fontWeight: 400 }}>
          {minDesktop ? (
            <>
              <ExtranetTableRow
                style={{
                  backgroundColor: 'var(--color-accent-4) ',
                  border: 'none',
                  fontWeight: 400,
                }}
              >
                <ExtranetTableColumn size={1}>{t('shop.orders.orderRow')}</ExtranetTableColumn>
                <ExtranetTableColumn size={2}>{t('shop.orders.productName')}</ExtranetTableColumn>
                <ExtranetTableColumn size={1}>{t('shop.orders.quantity')}</ExtranetTableColumn>
                <ExtranetTableColumn size={1}>{t('shop.orders.unitPrice')}</ExtranetTableColumn>
                <ExtranetTableColumn size={1}>{t('shop.orders.totalPrice')}</ExtranetTableColumn>
                <ExtranetTableColumn size={1}>{t('shop.orders.status')}</ExtranetTableColumn>
                <ExtranetTableColumn size={1}>
                  {t('billedDeliveries.deliveryNumber')}
                </ExtranetTableColumn>
                <ExtranetTableColumn size={0.5} />
              </ExtranetTableRow>
            </>
          ) : (
            <>
              <ExtranetContainer flexColumn>
                <PharmacText weight="bold"> {t('shop.orders.orderStatus')} </PharmacText>
                <PharmacText marginMediumBottom>
                  {statusTranslations(order.orderStatus)}
                </PharmacText>
              </ExtranetContainer>
              <ExtranetContainer flexColumn>
                <PharmacText weight="bold">{t('shop.orders.totalPrice')}</PharmacText>
                <PharmacText marginMediumBottom>
                  {formatPrice(order.totalSellingPrice!)}
                </PharmacText>
              </ExtranetContainer>
              <ExtranetContainer flexColumn>
                <PharmacText weight="bold">{t('shop.orders.purchaserName')}</PharmacText>
                <PharmacText marginMediumBottom>{order.purchaser}</PharmacText>
              </ExtranetContainer>
            </>
          )}
          {data?.map((a, index) => (
            <OrderItemRow
              data={a}
              key={`${a.orderRowNumber}-OrderItemRow-${index}`}
              toggleCallback={handleNestedExpand}
              expandedOrderItemRows={expandedOrderItemRows}
            ></OrderItemRow>
          ))}
        </ExtranetTable>
      </ExtranetContainer>
    </ExtranetContainer>
  )
}
